'use client'

/**
 * What kind of pointer is actually driving this session (Issue #13640).
 *
 * ## Why this is not `useIsMobile`
 *
 * `useIsMobile` is `window.innerWidth < breakpoint`. Width is a proxy for
 * "is this touch", and it is wrong in both directions on the devices that
 * matter: an iPad in landscape is 1024 CSS px — above every `md` gate written
 * to exclude it — and a half-width desktop window is 700, below one. A
 * surface that must know whether HOVER EXISTS has to ask whether hover
 * exists.
 *
 * `(hover: hover) and (pointer: fine)` is the conjunction, and both halves
 * earn their place: `(pointer: fine)` alone is satisfied by a stylus with no
 * hover state, and `(hover: hover)` alone by a device whose primary pointer
 * is a finger but which has some hovering secondary input. Together they mean
 * "the primary pointer can preview before it commits", which is precisely the
 * question `element-picker.tsx` needs answered.
 *
 * ## Reactive, because the answer changes under the user's hands
 *
 * An iPad gains a Magic Keyboard trackpad and loses it again, and Safari
 * re-evaluates the query when it does. Reading `matchMedia(...).matches` once
 * at mount would leave a tablet that just gained a trackpad on the touch
 * interaction until the next full reload. The `change` subscription is the
 * feature, not boilerplate.
 *
 * `useSyncExternalStore` rather than `useState` + `useEffect`: it gives a
 * declared SERVER snapshot, so the server render and the hydrating client
 * render agree by construction rather than by a first-render default that
 * someone later "simplifies" away. The subscription holds the SAME
 * `MediaQueryList` object for the add and the remove — listeners live on the
 * individual object, so unsubscribing through a fresh `matchMedia(...)` call
 * would remove nothing and leak a listener per mount.
 */

import { useSyncExternalStore } from 'react'

/** The one query. Exported so its test asserts the literal, not a paraphrase. */
export const FINE_POINTER_QUERY = '(hover: hover) and (pointer: fine)'

/**
 * `fine` — a mouse, a trackpad, a hovering pencil: it can preview before it
 * commits. `coarse` — a bare finger: the first contact is already a commit
 * unless something intercepts it.
 */
export type PointerCapability = 'fine' | 'coarse'

/**
 * One `MediaQueryList`, created lazily and reused — keyed on the `matchMedia`
 * function that produced it.
 *
 * `useSyncExternalStore` calls `getSnapshot` on every render and on every
 * store-change check, so constructing a fresh list per call re-parsed the
 * query and allocated an object each time. That was wasteful rather than
 * wrong (the snapshot is a primitive, so no render loop), and reusing one
 * object additionally makes `subscribe`'s teardown structurally real:
 * listeners live on the individual `MediaQueryList`, so removing through a
 * fresh `matchMedia(...)` call removes nothing and leaks a listener per mount.
 *
 * ## Why the cache is KEYED rather than a plain module-level memo
 *
 * The unit suite runs with `isolate: false`, so module state outlives a single
 * test file and reaches every later one in the same worker. A plain memo would
 * hand the SECOND test in a file the FIRST test's stubbed list — the tests
 * here stub `matchMedia` per case precisely to pin a capability — and the
 * failure would surface as an unrelated file's flake, not as this one's bug.
 *
 * Keying on the function identity removes the hazard without anyone having to
 * remember a reset hook: `vi.stubGlobal('matchMedia', …)` installs a NEW
 * function, the key no longer matches, and the list is rebuilt. Production has
 * exactly one `matchMedia` for the page's lifetime, so it caches on the first
 * call and never again.
 */
let cachedMatchMedia: typeof window.matchMedia | null = null
let cachedList: MediaQueryList | null = null

function fineListOrNull(): MediaQueryList | null {
  if (typeof window === 'undefined') return null
  const matchMedia = window.matchMedia
  if (typeof matchMedia !== 'function') return null
  if (cachedMatchMedia !== matchMedia) {
    cachedMatchMedia = matchMedia
    cachedList = matchMedia.call(window, FINE_POINTER_QUERY)
  }
  return cachedList
}

function subscribe(onStoreChange: () => void): () => void {
  const list = fineListOrNull()
  if (!list) return () => {}
  list.addEventListener('change', onStoreChange)
  return () => list.removeEventListener('change', onStoreChange)
}

function getSnapshot(): PointerCapability {
  const list = fineListOrNull()
  // No `matchMedia` on the CLIENT — a webview old enough to lack it. `coarse`
  // is the fail-safe answer, and the direction matters more than it looks:
  //
  //   wrong-way `fine` on a touch device — the confirm and step controls are
  //   not rendered and the first tap commits an anchor sight unseen, which is
  //   the entire defect #13640 exists to remove.
  //
  //   wrong-way `coarse` on a desktop — the viewer clicks twice instead of
  //   once and sees three extra buttons.
  //
  // A browser without `matchMedia` is also far likelier to be an old mobile
  // webview than a desktop, so the cheap failure is also the likely one.
  if (!list) return 'coarse'
  return list.matches ? 'fine' : 'coarse'
}

/**
 * The SERVER answer stays `fine`, and it is deliberately not the client
 * fallback above.
 *
 * There is no pointer on a server, so this value is about hydration, not about
 * capability: `fine` renders the smaller tree, and a real coarse client then
 * re-renders and ADDS the control bar. Declaring `coarse` here would ship the
 * coarse chrome in every SSR payload and then tear it back down on every
 * desktop, which is a visible flash on the majority path to protect nothing —
 * the client snapshot has already decided correctly by then.
 */
function getServerSnapshot(): PointerCapability {
  return 'fine'
}

/** The current primary-pointer capability, re-read whenever it changes. */
export function usePointerCapability(): PointerCapability {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}
